import { v4 as uuidv4 } from 'uuid';
import { UAParser } from 'ua-parser-js';
import Logger from '../logger';
import { DeviceFeatureDetection } from './detection/feature';
import { DeviceOSDetection } from './detection/os';
import { DeviceViewportDetection } from './detection/viewport';
import { DeviceModelDetection } from './detection/model';
import { DeviceBrowserDetection } from './detection/browser';
import { DeviceHybridDetection } from './detection/hybrid';

class Device {
    public logger: Logger;
    private _uaParser: UAParser = new UAParser();
    public readonly feature = new DeviceFeatureDetection();
    public readonly os = new DeviceOSDetection(this._uaParser);
    public readonly viewport = new DeviceViewportDetection();
    public readonly model = new DeviceModelDetection(this._uaParser);
    public readonly browser = new DeviceBrowserDetection(this._uaParser);
    public readonly hybrid = new DeviceHybridDetection(this.os);

    /**
     * Initialize the device.
     *
     * @constructor
     */
    constructor() {
        this.logger = new Logger((this.constructor as any).CLASS);
    }

    /**
     * Getter for the user-agent for the client being handled in this request.
     *
     * @example 'Mozilla/5.0 (iPad; CPU OS 7_0 like Mac OS X) AppleWebKit/537.51.1 (KHTML, like Gecko) Version/7.0 Mobile/11A465 Safari/9537.53'
     */
    public get userAgent() {
        return this._uaParser.getUA();
    }

    public get networkData() {
        let connection = navigator["connection"] || navigator["mozConnection"] || navigator["webkitConnection"];
        if(connection) {
            return (connection.effectiveType?connection.effectiveType:'') + '_'
                + (connection.rtt?connection.rtt:'') + '_'
                + (connection.downlink?connection.downlink:'');
        }
        return null;
    }

    /**
     * Generates a GUID
     */
    public generateGuid(): string {
        return uuidv4();
    }

    private matchMedia(media: string) {
        return window.matchMedia(media).matches;
    }

    /**
     * Used to detected if user clicked and loaded the app using home screen
     *
     * Returns true if the app is running in standalone mode (iOS only)
     */
    public get isStandalone()  {
        return 'standalone' in window.navigator ? (window.navigator as any).standalone : false;
    }

    public get isProgressiveWebApp(): boolean {
		// PWA setting is "display": "fullscreen"
		// but installed on desktop it's detected as "standalone" if app is not in fullscreen mode or "fullscreen" if user enters fullscreen mode
        // iOS is identified through check the navigator to have attribute 'standalone'

		return !this.hybrid.isHybrid && (this.isStandalone ||
            this.matchMedia('(display-mode: fullscreen)') ||
            this.matchMedia('(display-mode: standalone)'));

    }

    public get isSupportHTML5() {
        return 'function' === typeof document.createElement('canvas').getContext;
    }

    public get isMobileWebApp() {
        return (this.os.isMobileOS || this.isProbablyIOSDevice) && !this.hybrid.isHybrid;
    }

    /**
     * Check whether the browser has cookie enable 3rd party.
     */
    public get isCookieEnable() {
        return window.navigator.cookieEnabled;
    }

    /**
     * From iOS 13, Safari decided to put the "Request Desktop Website" setting on true by default on iPad.
     * For users that disable this setting, Device.model.isIpad will be true, but for the majority it will be false because
     * Safari will send the desktop user agent.
     *
     * Also, iPhone users have that option also although not many people enable it.
     *
     * The only clue we have that it is most probably an iPad is to detect if the device has touchscreen because up until now
     * Apple does not produce MacBooks or Macs with touchscreens. So it's most probably an iOS (iPad or an iPhone)
     *
     * @Note IMPORTANT! Should be use only in rare cases (for example force GRS(GGP games) to return the mobile version on iPad)
     * @Note IMPORTANT! This method should return true for all iOS and iOS + "Request Desktop Website", iPhone INCLUDED
     */
    public get isProbablyIOSDevice() {
        if (this.os.isIOS) {
            return true;
        }
        if (!this.os.isMacOS) {
            return false;
        }
        // At this point we know that we are on Mac
        // Let's see if it has touchscreen
        return this.feature.isTouch;
    }

    /**
     * From iOS 13, Safari decided to put the "Request Desktop Website" setting on true by default on iPad.
     * For users that disable this setting, Device.model.isIpad will be true, but for the majority it will be false because
     * Safari will send the desktop user agent.
     *
     * Also, iPhone users have that option also although not many people enable it.
     *
     * The only clue we have that it is most probably an iPad is to detect if the device has touchscreen because up until now
     * Apple does not produce MacBooks or Macs with touchscreens. So it's most probably an iOS (iPad or an iPhone)
     *
     * @Note IMPORTANT! Should be use only in rare cases (for example force GRS(GGP games) to return the mobile version on iPad)
     * @Note IMPORTANT! This method should return true for all iPad and iOS + "Request Desktop Website", iPhone NOT INCLUDED
     * HOWEVER, FOR IPHONE THAT HAVE "Request Desktop Website", this method will return true
     */
    public get isProbablyIPad() {
        // Pre iOS 13 detection
        if (this.model.isIpad) {
            return true;
        }
        if (this.model.isIphone) {
            return false;
        }
        if (!this.os.isMacOS) {
            return false;
        }
        // At this point we know that is a Safari browser on Mac (in the past, Safari was available for Windows too)
        // Let's see if it has touchscreen
        return this.feature.isTouch;
    }

    /**
     * Check the device is in a portrait mode.
     *
     * @deprecated use "Device.viewport.isPortrait"
     */
    public get isPortrait(): boolean {
        if (this.isMobile() && this.os.isAndroid) {
            // fix for Samsung S8 series (S8-S9...).
            // In full screen mode window.screen.width and window.screen.height after single rotation get wrong values (width equal to height)
            // So we should test it by innerWidth and innerHeight like in iOS
            if (this.viewport.screenWidth === this.viewport.screenHeight) {
                return this.viewport.width < this.viewport.height;
            }
            return this.viewport.screenWidth < this.viewport.screenHeight;
        } else {
            return this.viewport.width < this.viewport.height;
        }
    }

    /**
     * TODO: Replace with NOTCH DETECTION. We don't care about the iPhone version, we care to know for iPhone X if it has a notch.
     *
     * Getting the current running iPhone device version, hardware version.
     * Able to detect iPhone 4 and 5 and iphone x (10)
     * Returns NULL if device is not an iPhone. Will return a Number of the hardware version.
     * @deprecated
     */
    public getIphoneVersion() {

        if (this.os.isIOS && 11 <= this.os.version &&
            (
                //iphone 12 pro max
                (926 === window.screen.height && 428 === window.screen.width) || (926 === window.screen.width && 428 === window.screen.height)
                ||
                //iphone 12 pro
                (844 === window.screen.height && 390 === window.screen.width) || (844 === window.screen.width && 390 === window.screen.height)
                ||
                // iPhone X / Xs
                (812 === window.screen.height && 375 === window.screen.width) || (812 === window.screen.width && 375 === window.screen.height)
                ||
                // iPhone Xr / Xs Max
                (896 === window.screen.height && 414 === window.screen.width) || (896 === window.screen.width && 414 === window.screen.height)
            )
        ) {
            return 10;
        }

        if (this.os.isIOS && 8 <= this.os.version && 375 === window.screen.width) {
            return 6;
        }

        if (this.os.isIOS && 6 <= this.os.version && 320 === window.screen.width) {
            return 5;
        }

        // iphone 4/s (Retina)
        if (this.os.isIOS && 5 <= this.os.version && 640 === window.screen.width) {
            return 4;
        }

        return 0;
    }

    /**
     * TODO: Replace with NOTCH DETECTION. We don't care about the iPhone version, we care to know for iPhone X if it has a notch.
     * Is IphoneX
     * Returns true if the device is IphoneX
     * @deprecated
     */
    public isIphoneX(): boolean {
        return 10 === this.getIphoneVersion();
    }

    /**
     * TODO: Check usages and delete/refactor
     * @deprecated
     */
    public isMobileNotTablet(): boolean {
        return this.viewport.width < 1280 && !this.isTablet();
    }


    /**
     * TODO: Check usages and delete/refactor
     * Returns true only for ipads smaller than 1280
     * @deprecated
     */
    public isTablet(): boolean {
        return this.model.isIpad && this.viewport.width < 1280;
    }

    /**
     * Those are not the droids you are looking for
     * Use media queries, feature detection, client hints, os detection.
     * Think of what you want to achieve with your code and put a condition for that, not a general isMobile condition
     *
     * TODO: Check usages and delete/refactor
     * @deprecated
     */
    public isMobile(): boolean {
        return this.viewport.width < 1280;
    }

    /**
     * Get benchmark.
     */
    public getBenchMark() {
        var b = { m: 0 }, bd = [], bmt = + new Date() + 50;
        do {
            var t, count = 0, tt = + new Date();
            do { t = + new Date(); } while (t === tt);
            do { count++; tt = +new Date(); } while (t === tt);
            if ((tt - t) === 1) { bd[bd.length] = count; }
        } while ((bd.length < 20) && (t < bmt));

        if (bd.length === 0) b.m = 0; else b.m = Math.max.apply(Math, bd);

        return b.m;
    }

}

export default new Device();
